import { SelectionModel } from '@angular/cdk/collections';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Observable, Subject, asyncScheduler, isObservable, of } from 'rxjs';
import { concatMap, observeOn, startWith } from 'rxjs/operators';

export interface TypeInfo {
  getType(propertyName: string): string;
}

export const hasTypeInfo = (obj: any): obj is TypeInfo => (obj as TypeInfo).getType !== undefined;

export type OriginalValues<T> = Partial<Omit<T, keyof EntityStatus<T>>>;

export interface EntityStatus<T> {
  isNew: boolean;
  isModified: boolean;
  isDeleted: boolean;
  isDetached: boolean;
  originalValues: OriginalValues<T>;
  isMatch: boolean;
  isDuplicate: boolean;
  isCeased: boolean;
}

export interface ElementMergeArgs {
  source: any;
  target: any;
}

@Component({
  selector: 'insol-edit-table',
  templateUrl: './edit-table.component.html',
})
export class EditTableComponent implements OnChanges {
  @Input() columns: string[];
  @Input() columnsDisplay: string[];
  @Input() enableDelete: boolean;
  @Input() enableEntityStatus: boolean;
  @Input() enableMerge: boolean;
  @Input() readOnly: boolean;
  @Output() handleDelete = new EventEmitter<any>();
  @Output() handleUndoDelete = new EventEmitter<any>();
  @Output() handleUndo = new EventEmitter<any>();
  @Output() handleMerge = new EventEmitter<ElementMergeArgs>();

  dataSource = new MatTableDataSource<any>([]);
  allColumns: string[];
  selection = new SelectionModel<any>();

  private _data: Observable<any[]> | any[];
  private dataChange$ = new Subject<Observable<any> | any[]>();

  get data(): Observable<any[]> | any[] {
    return this._data;
  }
  @Input() set data(value: Observable<any[]> | any[]) {
    this._data = value;
    this.dataChange$.next(value);
  }

  get hasElementsToMerge(): boolean {
    return this.dataSource.data.some((element: EntityStatus<any>) => element.isNew);
  }

  constructor() {
    this.dataChange$
      .pipe(
        startWith(this.data),
        concatMap((data) => {
          if (isObservable(data)) {
            return data as Observable<any[]>;
          }
          return of(data as any[]);
        })
      )
      .pipe(observeOn(asyncScheduler))
      .subscribe((data) => {
        this.dataSource.data = data || [];
      });
  }

  ngOnChanges(): void {
    let allColumns = [];
    if (this.enableMerge && !this.readOnly) {
      allColumns = ['$select'];
    }
    if (this.enableEntityStatus) {
      allColumns = [...allColumns, '$entityStatus'];
    }
    if (this.enableDelete && !this.readOnly) {
      allColumns = [...allColumns, '$action'];
    }
    this.allColumns = [...allColumns, ...this.columns];
  }

  type(element: any, column: string): string {
    if (hasTypeInfo(element)) {
      return element.getType(column);
    }

    const value = element[column];
    if (value && Array.isArray(value)) {
      return 'array';
    } else if (value && value instanceof Date && !isNaN(value.valueOf())) {
      return 'DateTime';
    } else {
      return typeof value;
    }
  }

  longValueName(shortValueName: string): string {
    if (shortValueName.startsWith('$short')) {
      return shortValueName.replace('$short', '$long');
    }

    return null;
  }

  display(column: string): string {
    const index = (this.columns || []).indexOf(column);
    if (index === -1) {
      return '';
    }

    return this.columnsDisplay[index];
  }

  delete(element: any): void {
    if (!this.enableDelete || this.readOnly) {
      return;
    }
    this.handleDelete.next(element);
  }

  undoDelete(element: any): void {
    if (!this.enableDelete || this.readOnly) {
      return;
    }
    this.handleUndoDelete.next(element);
  }

  undo(element: any): void {
    if (this.readOnly) {
      return;
    }
    this.handleUndo.next(element);
  }

  merge(element: any): void {
    if (!this.enableMerge || this.readOnly) {
      return;
    }
    this.handleMerge.next({
      source: element,
      target: this.selection.selected[0],
    });
  }
}
